/**
 * Compliance with European Union privacy law.
 * @module privacyCompliance
 */

import EU_PRIVACY_COUNTRIES from './euPrivacyLocales';
import OPT_OUT_PRIVACY_COUNTRIES from './optOutPrivacyLocales';

import {
  getAllCookieString,
  parseCookies,
  getCookieValue,
} from './cookies';

const CONSENT_COOKIE_NAME = 'CookieConsent';
const OPT_OUT_COOKIE_NAME = 'ucmdnuc';

const CONSENT_VALUE_REGEX_FRAGMENT = '[spta]:[yn]';

/** Matches this as a whole: "S:Y,P:Y,T:Y,A:Y". */
const ALL_CONSENT_VALUE_REGEXP = new RegExp(
  `^(${CONSENT_VALUE_REGEX_FRAGMENT},){3}${CONSENT_VALUE_REGEX_FRAGMENT}$`,
  'i'
);

/** Matches "S:Y". */
const ONE_CONSENT_VALUE_REGEXP = new RegExp(CONSENT_VALUE_REGEX_FRAGMENT, 'gi');

/** Matches "en-GB" or "GB_en" */
const PAGE_LOCALE_REGEXP = /^([a-z]{2,3})([_-])([a-z]{2,3})$/i;

const Y_N_TO_BOOLEAN = {
  y: true,
  n: false,
};

const CLASSIFICATION_LETTER_TO_WORD = {
  s: 'essentialAllowed',
  p: 'performanceAllowed',
  t: 'functionalAllowed',
  a: 'amexMarketingAllowed',
};

/** Used when user has not accepted/declined yet */
const DEFAULT_CONSENT_VALUE_OBJECT = {
  essentialAllowed: true,
  performanceAllowed: false,
  functionalAllowed: false,
  amexMarketingAllowed: false,
};

/** Converts values like 'en-GB" to "GB_en". Copied from
 * {@link https://qicm.aexp-static.com/content/dam/Navigation/nav/ngn/js/euc_cookie.js}
 */
const normalizePageLocale = (pageLocale) => {
  const match = pageLocale.trim().toLowerCase().match(PAGE_LOCALE_REGEXP);
  const [whole, first, separator, second] = match;

  let country = '';
  let language = '';

  // istanbul ignore else
  if (separator === '_') {
    if (whole === 'ei_en' || whole === 'eu_en' || whole === 'eu_sp') {
      country = 'icc';
      language = 'en';
    } else if (whole === 'no_no' || whole === 'no_nb') {
      country = 'no';
      language = 'nb';
    } else {
      country = first;
      language = second;
    }
  } else if (separator === '-') {
    if (whole === 'en-xa' || whole === 'en-xb') {
      country = 'icc';
      language = 'en';
    } else if (whole === 'no-no' || whole === 'nb-no') {
      country = 'no';
      language = 'nb';
    } else {
      country = second;
      language = first;
    }
  }

  return `${country.toUpperCase()}_${language.toLowerCase()}`;
};

/** Returns something like "GB_en%3DS%3AY%2CP%3AY%2CT%3AY%2CA%3AY"
 * or "US%3DS%3AY%2CP%3AY%2CT%3AY%2CA%3AY"
*/
const getFullConsentCookie = (locale) => {
  const allCookieString = getAllCookieString();
  const cookiesObject = parseCookies(allCookieString);

  const normalizedLocale = normalizePageLocale(locale);
  const country = normalizedLocale.split('_')[0];

  let consentCookie;
  if (OPT_OUT_PRIVACY_COUNTRIES.some((privacyCountry) => privacyCountry === country)) {
    consentCookie = getCookieValue(cookiesObject, OPT_OUT_COOKIE_NAME);
  } else {
    consentCookie = getCookieValue(cookiesObject, CONSENT_COOKIE_NAME);
  }
  return consentCookie;
};

/** Returns something like "S:Y,P:Y,T:Y,A:Y" */
const findConsentTextForLocale = (decodedConsentCookie, locale) => {
  const filteredConsentValues = decodedConsentCookie.split('|').map((localeAndValue) => {
    const afterSplit = localeAndValue.trim().split('=');
    const [thisLocale, thisConsentValue] = afterSplit;

    const country = locale.split('_')[0];

    if ((OPT_OUT_PRIVACY_COUNTRIES.some((privacyCountry) => privacyCountry === country)
        && thisLocale.trim().toLowerCase() === country.trim().toLowerCase())
        || thisLocale.trim().toLowerCase() === locale.trim().toLowerCase()) {
      return thisConsentValue;
    }

    return null;
  }).filter((thisConsentValue) => typeof thisConsentValue === 'string');

  if (filteredConsentValues.length === 0) {
    return '';
  }

  return filteredConsentValues[0];
};

/** Returns something like {@link DEFAULT_CONSENT_VALUE_OBJECT} */
const parseConsentCookieForLocale = (decodedConsentCookie, locale) => {
  const consentText = findConsentTextForLocale(decodedConsentCookie, locale);
  if (!ALL_CONSENT_VALUE_REGEXP.test(consentText)) {
    return DEFAULT_CONSENT_VALUE_OBJECT;
  }

  return consentText.match(ONE_CONSENT_VALUE_REGEXP).map((letterAndYesNo) => {
    const [letter, yesNo] = letterAndYesNo.split(':');
    const word = CLASSIFICATION_LETTER_TO_WORD[letter.toLowerCase()];
    const isYes = Y_N_TO_BOOLEAN[yesNo.toLowerCase()];
    return { [word]: isYes };
  }).reduce((acc, x) => ({ ...acc, ...x }), {});
};

/**
 * Returns true if the given country is governed by EU privacy law and therefore we need the user's
 * consent before we load certain scripts on the page.
 * @returns {boolean}
 */
const needUserConsentForLocale = (locale) => {
  const normalizedLocale = normalizePageLocale(locale);
  const country = normalizedLocale.split('_')[0];
  let needConsent = false;
  if (EU_PRIVACY_COUNTRIES.some((privacyCountry) => privacyCountry === country)
      || (OPT_OUT_PRIVACY_COUNTRIES.some((privacyCountry) => privacyCountry === country)
          && getFullConsentCookie(locale))) {
    needConsent = true;
  }
  return needConsent;
};

/**
 * Returns true if we have the user's consent to load this script on the page. Also returns true if
 * script supplier should load the script anyway because it's not responsible for enforcing user
 * consent for this particular script.
 * @param {Object} parsedConsentValue - Get from {@link parseConsentCookieForLocale}.
 * @param {Object} scriptClassifications - See {@link validateScriptClassifications}.
 * @returns {boolean}
 */
const haveUserConsentForScript = (parsedConsentValue, scriptClassifications) => {
  const {
    performanceAllowed,
    amexMarketingAllowed,
    functionalAllowed,
  } = parsedConsentValue;
  const {
    essential,
    functional,
    performance,
    amexMarketing,
    thirdPartyMarketing,
    scriptSupplierHandlesConsent,
  } = scriptClassifications;

  if (scriptSupplierHandlesConsent !== undefined && !scriptSupplierHandlesConsent) {
    return true;
  }

  // If any of the script's classifications are allowed, load the script and expect downstream
  // systems to properly handle consent at a more granular level.
  if (essential
    || (functional && functionalAllowed)
    || (performance && performanceAllowed)
    || (amexMarketing && amexMarketingAllowed)
    || (thirdPartyMarketing && amexMarketingAllowed)) {
    return true;
  }

  return false;
};

const getPrivacySingletonObject = () => {
  if (typeof window.scriptSupplierPrivacySingleton !== 'object'
    || window.scriptSupplierPrivacySingleton === null) {
    window.scriptSupplierPrivacySingleton = {};
  }
  return window.scriptSupplierPrivacySingleton;
};

const getDeniedScriptStore = (privacySingleton) => {
  if (typeof privacySingleton.deniedScriptStore !== 'object'
    || privacySingleton.deniedScriptStore === null) {
    // eslint-disable-next-line no-param-reassign -- TODO: Replace
    privacySingleton.deniedScriptStore = {};
  }
  return privacySingleton.deniedScriptStore;
};

/** Stores a script that was not allowed to load because the user hasn't given consent yet. This way
 * we can load it later when the user does give consent.
 */
const saveDeniedScript = ({
  name,
  version,
  classifications,
  loadOnPageFn,
  deniedScriptStore,
  pageLocale,
}) => {
  if (typeof deniedScriptStore[name] !== 'object'
    || deniedScriptStore[name] === null) {
    // eslint-disable-next-line no-param-reassign -- TODO: Replace
    deniedScriptStore[name] = {};
  }

  // eslint-disable-next-line no-param-reassign -- TODO: Replace
  deniedScriptStore[name][version] = {
    classifications,
    loadOnPageFn,
    pageLocale,
  };
};

const getAllDeniedScripts = (deniedScriptStore) => {
  return Object.keys(deniedScriptStore).map((name) => {
    return Object.keys(deniedScriptStore[name]).map((version) => {
      return {
        name,
        version,
        ...deniedScriptStore[name][version],
      };
    });
  }).reduce((acc, x) => [...acc, ...x], []);
};

/** Load a single script on the page which were previously not allowed to load but are allowed now
 * because the user just gave consent.
 */
const processDeniedScript = (deniedScript, decodedConsentCookie) => {
  const {
    classifications,
    loadOnPageFn,
    pageLocale,
  } = deniedScript;

  const locale = normalizePageLocale(pageLocale);
  const parsedConsentValue = parseConsentCookieForLocale(decodedConsentCookie, locale);
  if (haveUserConsentForScript(parsedConsentValue, classifications)) {
    loadOnPageFn();
  }
};

const processAllDeniedScripts = (deniedScripts, decodedConsentCookie) => {
  deniedScripts.forEach((deniedScript) => {
    processDeniedScript(deniedScript, decodedConsentCookie);
  });
};

/** Handler for event fired when user clicks "Accept" or "Decline" on the cookie consent banner.
 */
const consentChangeHandler = (decodedConsentCookie) => {
  if (!decodedConsentCookie) {
    return;
  }
  const privacySingleton = getPrivacySingletonObject();
  const deniedScriptStore = getDeniedScriptStore(privacySingleton);
  const deniedScripts = getAllDeniedScripts(deniedScriptStore);
  processAllDeniedScripts(deniedScripts, decodedConsentCookie);
};

/** Add a handler for Script Supplier if it's not already present.
 */
export const registerConsentChangeHandler = () => {
  if (getPrivacySingletonObject().consentChangeHandlerRegistered) {
    return;
  }

  // TODO: Call this on UCM.
  if (!Array.isArray(window.EuCookieConsentHandlers)) {
    window.EuCookieConsentHandlers = [];
  }

  window.EuCookieConsentHandlers.push(consentChangeHandler);
  getPrivacySingletonObject().consentChangeHandlerRegistered = true;
};

// TODO: Move this into UCM
/** Returns true if it's ok to load the given script based on privacy regulations
 * and whether the user has given their consent to have their data tracked and
 * used for marketing, analytics, etc.
 */
export const isOkToLoadScript = (scriptName, scriptClassifications, pageLocale) => {
  const locale = normalizePageLocale(pageLocale);
  if (!needUserConsentForLocale(locale)) {
    return true;
  }

  const consentCookie = getFullConsentCookie(locale);
  const decodedConsentCookie = decodeURIComponent(consentCookie);
  const parsedConsentValue = parseConsentCookieForLocale(decodedConsentCookie, locale);

  return haveUserConsentForScript(parsedConsentValue, scriptClassifications);
};

/** Should be called for a script which was not allowed to load on the page because
 * {@link isOkToLoadScript} returned false. This way we can load it later when the user provides
 * their consent by clicking the "Accept" button on the cookie consent banner.
 */
export const scriptWasDenied = (args) => {
  const {
    name,
    version,
    classifications,
    pageLocale,
    loadOnPageFn,
  } = args;
  const privacySingleton = getPrivacySingletonObject();
  const deniedScriptStore = getDeniedScriptStore(privacySingleton);
  saveDeniedScript({
    name,
    version,
    classifications,
    loadOnPageFn,
    deniedScriptStore,
    pageLocale,
  });
};

export const unitTestExports = {
  CONSENT_COOKIE_NAME,
  OPT_OUT_COOKIE_NAME,
  ALL_CONSENT_VALUE_REGEXP,
  ONE_CONSENT_VALUE_REGEXP,
  PAGE_LOCALE_REGEXP,
  DEFAULT_CONSENT_VALUE_OBJECT,
  getFullConsentCookie,
  findConsentTextForLocale,
  parseConsentCookieForLocale,
  normalizePageLocale,
  needUserConsentForLocale,
  haveUserConsentForScript,
  getPrivacySingletonObject,
  getDeniedScriptStore,
  saveDeniedScript,
  getAllDeniedScripts,
  processDeniedScript,
  processAllDeniedScripts,
  consentChangeHandler,
};
